import React, { useState, useEffect } from "react";
import { useNavigation } from "context/NavigationContext";
import {
  BringzzPageContainer,
  BringzzButton,
  BringzzIcon,
  BringzzText,
  BringzzInput,
  BringzzDropdown,
} from "@bringzz/components";
import useRequest from "hooks/useRequest";
import { useLocation } from "react-router-dom";

const roles = [
  { value: "PKA", label: "PKA" },
  { value: "PTA", label: "PTA" },
];

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const EditPharmacyEmployeeInfo = () => {
  const { backPress, navigate } = useNavigation();
  const { sendRequest, loading } = useRequest();
  const query = useQuery();
  const belongs_to = query.get("belongs_to");

  const [pharmacyEmployee, setPharmacyEmployee] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const fetchEmployeeDetails = async () => {
      try {
        const response = await sendRequest({
          method: "GET",
          url: "/module/pharmacyteam/getPharmacyTeamMemberById",
          params: {
            belongs_to,
          },
        });

        if (response?.status === "success") {
          const employeeDetails = response.data;
          setPharmacyEmployee({
            ...employeeDetails,
            bio: employeeDetails.bio,
            firstName: employeeDetails.userDetails?.firstName || '',
            lastName: employeeDetails.userDetails?.lastName || '',
            role: employeeDetails.userRole || '',
            preferredLanguage: employeeDetails.userDetails?.preferredLanguage || '',
          });
        } else {
          setErrorMessage(response?.message || "Failed to fetch employee data.");
        }
      } catch (error) {
        console.error("Error fetching employee data:", error);
        setErrorMessage("An error occurred while fetching employee data.");
      }
    };

    fetchEmployeeDetails();
  }, [belongs_to, sendRequest]);

  const handleSubmit = async () => {
    if (
      !pharmacyEmployee.firstName.trim() ||
      !pharmacyEmployee.lastName.trim()
    ) {
      setErrorMessage("First Name and Last Name are required.");
      return;
    }

    try {
      const response = await sendRequest({
        method: "POST",
        url: "/module/pharmacyteam/editPharmacyTeamMember",
        data: {
          data: {
            teamMemberId: pharmacyEmployee.id,
            firstName: pharmacyEmployee.firstName,
            lastName: pharmacyEmployee.lastName,
            login: pharmacyEmployee.login,
            logintype: pharmacyEmployee.loginType,
            role: pharmacyEmployee.role,
            bio: pharmacyEmployee.bio,
          },
        },
      });

      if (response?.status === "success") {
        navigate("/settings/pharmacy/team", false);
      } else {
        setErrorMessage(
          response?.message || "An error occurred. Please try again."
        );
      }
    } catch (error) {
      console.error("Error updating employee:", error);
      setErrorMessage("An error occurred. Please try again.");
    }
  };

  if (!pharmacyEmployee) {
    return (
      <BringzzPageContainer
        header={{
          left: (
            <BringzzIcon
              size="22"
              icon="IconChevronLeft"
              folder="LineIcons"
              className="cursor-pointer"
              onClick={backPress}
            />
          ),
          title: <BringzzText tag="h2">Edit Employee</BringzzText>,
        }}
      >
        {errorMessage 
          ? <div className="flex justify-center py-4">
              <BringzzText tag="h3" className="text-red-500">{errorMessage}</BringzzText>
            </div>
          : <div className="flex justify-center py-4">
              <BringzzText tag="h3">Loading employee details...</BringzzText>
            </div>}
      </BringzzPageContainer>
    );
  }

  return (
    <BringzzPageContainer
      header={{
        left: (
          <BringzzIcon
            size="22"
            icon="IconChevronLeft"
            folder="LineIcons"
            className="cursor-pointer"
            onClick={backPress}
          />
        ),
        title: <BringzzText tag="h2">Edit Employee</BringzzText>,
      }}
      footer={
        <div className="pb-28 px-6">
          <BringzzButton
            size="lg"
            className="bg-magic-lilac w-full flex justify-center text-midnight-blue"
            onClick={handleSubmit}
            disabled={loading}
          >
            Save Changes
          </BringzzButton>
        </div>
      }
    >
      <div className="p-6 space-y-4">
        {errorMessage && (
          <BringzzText tag="h3" className="text-red-500">
            {errorMessage}
          </BringzzText>
        )}
        <div>
          <BringzzText tag="label" className="block mb-1">
            First Name
          </BringzzText>
          <BringzzInput
            value={pharmacyEmployee.firstName}
            onChange={(e) =>
              setPharmacyEmployee({
                ...pharmacyEmployee,
                firstName: e.target.value,
              })
            }
          />
        </div>
        <div>
          <BringzzText tag="label" className="block mb-1">
            Last Name
          </BringzzText>
          <BringzzInput
            value={pharmacyEmployee.lastName}
            onChange={(e) =>
              setPharmacyEmployee({
                ...pharmacyEmployee,
                lastName: e.target.value,
              })
            }
          />
        </div>
        <div>
          <BringzzText tag="label" className="block mb-1">
            Role
          </BringzzText>
          <BringzzDropdown
            options={roles}
            defaultValue={pharmacyEmployee.role}
            onChange={(value) =>
              setPharmacyEmployee({
                ...pharmacyEmployee,
                role: value,
              })
            }
          />
        </div>
        <div>
          <BringzzText tag="label" className="block mb-1">
            Bio
          </BringzzText>
          <BringzzInput
            type="textarea"
            value={pharmacyEmployee.bio}
            onChange={(e) =>
              setPharmacyEmployee({
                ...pharmacyEmployee,
                bio: e.target.value,
              })
            }
          />
        </div>
      </div>
    </BringzzPageContainer>
  );
};

export default EditPharmacyEmployeeInfo;
